/**
 * GPU side of the light field — docs/01-architecture.md §6.3.
 *
 * `sim/light.ts` owns the field; this owns exactly one texture and the two
 * uniforms needed to address it. Deliberately **not** one texture per chunk:
 * per-chunk textures sampled with LINEAR filtering seam at every chunk border
 * because CLAMP_TO_EDGE cannot see the neighbour's first texel, and a visible
 * seam every 128 cells is worse than no light at all. One contiguous window
 * over the visible chunk range has no interior edges to clamp against.
 *
 * The window is 16 texels per chunk axis (`LIGHT_PER_AXIS`), so a 480×270
 * viewport plus a one-chunk margin is at most 112×96 texels — 43 KB re-uploaded
 * whole every frame. Tracking dirty sub-rects here would cost more bookkeeping
 * than the upload it saves; the terrain textures are 1.5 MB and are the reason
 * that pass is tiled, and this one is not in the same regime.
 *
 * LINEAR is the one place in this renderer where filtering is correct. Terrain
 * and bodies are NEAREST because they are pixel art and a filtered material id
 * is meaningless; light is a low-frequency field being deliberately upsampled,
 * and bilinear interpolation is what makes an 8-cell tile grid read as a smooth
 * falloff rather than as blocks.
 */

import { LIGHT_PER_AXIS, LIGHT_TILE, type LightField } from '../sim/light';
import { CHUNK_SHIFT, CHUNK_SIZE } from '../sim/world';
import type { Camera } from './camera';

/** Largest window we will ever upload, in chunks per axis. */
const MAX_CHUNKS = 10;
const MAX_TEXELS = MAX_CHUNKS * LIGHT_PER_AXIS;

export class LightTexture {
  private gl: WebGL2RenderingContext;
  private tex: WebGLTexture;
  private data = new Uint8Array(MAX_TEXELS * MAX_TEXELS * 4);

  /**
   * Light floor — the `uAmbient` both shaders multiply material colour by.
   *
   * **The game is expected to drive this every frame** from
   * `world/lighting.ts` `ambientAt(cameraY)`, which is where the actual values
   * live: ambient is a property of depth, not a constant (issue #31).
   *
   * The default is 1.0, i.e. "render exactly as the game did before the field
   * existed", and that choice is deliberate. This shipped at 0.35, which #31
   * measured as near-black in every biome that has no fire in it — so an
   * integrator who forgets to set this gets a flat, bright, playable world
   * rather than a black screen. A missing wire should fail toward legible.
   */
  ambient = 1;

  /** Multiplier on the field's own contribution, for LOD and for debug. */
  gain = 1;

  /** Window origin in world cells, and the reciprocal of its extent. */
  originX = 0;
  originY = 0;
  scaleX = 1;
  scaleY = 1;

  private texW = 0;
  private texH = 0;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    this.tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA8, MAX_TEXELS, MAX_TEXELS);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  /** Pull the visible window out of the field and upload it. */
  update(field: LightField, camera: Camera): void {
    const view = camera.visibleRect(CHUNK_SIZE);
    const cx0 = Math.floor(view.x0) >> CHUNK_SHIFT;
    const cy0 = Math.floor(view.y0) >> CHUNK_SHIFT;
    let cw = (Math.floor(view.x1) >> CHUNK_SHIFT) - cx0 + 1;
    let ch = (Math.floor(view.y1) >> CHUNK_SHIFT) - cy0 + 1;
    if (cw > MAX_CHUNKS) cw = MAX_CHUNKS;
    if (ch > MAX_CHUNKS) ch = MAX_CHUNKS;

    const w = cw * LIGHT_PER_AXIS;
    const h = ch * LIGHT_PER_AXIS;
    const data = this.data;

    for (let k = 0; k < ch; k++) {
      for (let j = 0; j < cw; j++) {
        const tiles = field.tiles(cx0 + j, cy0 + k);
        const ox = j * LIGHT_PER_AXIS;
        const oy = k * LIGHT_PER_AXIS;
        if (tiles === null) {
          // An unlit chunk is black, not absent: the shader adds ambient, so
          // "no light here" and "no tile grid here" have to look the same.
          for (let ty = 0; ty < LIGHT_PER_AXIS; ty++) {
            let d = ((oy + ty) * w + ox) * 4;
            for (let tx = 0; tx < LIGHT_PER_AXIS; tx++) {
              data[d] = 0;
              data[d + 1] = 0;
              data[d + 2] = 0;
              data[d + 3] = 255;
              d += 4;
            }
          }
          continue;
        }
        for (let ty = 0; ty < LIGHT_PER_AXIS; ty++) {
          let s = ty * LIGHT_PER_AXIS * 3;
          let d = ((oy + ty) * w + ox) * 4;
          for (let tx = 0; tx < LIGHT_PER_AXIS; tx++) {
            data[d] = byte(tiles[s]);
            data[d + 1] = byte(tiles[s + 1]);
            data[d + 2] = byte(tiles[s + 2]);
            data[d + 3] = 255;
            s += 3;
            d += 4;
          }
        }
      }
    }

    this.originX = cx0 << CHUNK_SHIFT;
    this.originY = cy0 << CHUNK_SHIFT;
    this.scaleX = 1 / (w * LIGHT_TILE);
    this.scaleY = 1 / (h * LIGHT_TILE);
    this.texW = w;
    this.texH = h;

    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, data, 0);
  }

  bind(unit: number): void {
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
  }

  get width(): number {
    return this.texW;
  }

  get height(): number {
    return this.texH;
  }
}

function byte(v: number): number {
  const n = v * 255;
  return n <= 0 ? 0 : n >= 255 ? 255 : n | 0;
}

/**
 * The 1×1 white texture bound when no light field is supplied.
 *
 * Both shaders declare `uLight` unconditionally, and a `sampler2D` uniform left
 * pointing at texture unit 0 would be reading whatever integer texture the
 * terrain pass bound there — undefined behaviour on some drivers, and a black
 * screen on the rest. Gain is zero in that path so the value never matters;
 * what matters is that something float-sampleable is bound.
 */
export function createUnlitTexture(gl: WebGL2RenderingContext): WebGLTexture {
  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA8, 1, 1);
  gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE,
    new Uint8Array([255, 255, 255, 255]));
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  return tex;
}
